import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';
import { KV, KVRow } from '../components/KV';
import BookingsList from '../bookings/BookingsList';
import UsagePanel from '../usage/UsagePanel';
import SchemaForm from '../schema_form/SchemaForm';
import TenantOfficeActions from '../components/TenantOfficeActions';

function AddUserToTenantForm({
  tenant_id,
  on_created,
}: {
  tenant_id: string;
  on_created: () => void;
}) {
  const [first, set_first] = useState('');
  const [last, set_last] = useState('');
  const [email, set_email] = useState('');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    set_busy(true);
    set_error(null);
    try {
      await api.post('/api/seed/user-in-tenant', {
        tenant_id,
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim(),
      });
      on_created();
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_busy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="card"
      style={{ margin: '12px 0', display: 'grid', gap: 12, maxWidth: 540 }}
    >
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        Creates the user directly under this tenant (no company-name resolution).
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>First name</span>
          <input value={first} onChange={(e) => set_first(e.target.value)} required maxLength={80} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Last name</span>
          <input value={last} onChange={(e) => set_last(e.target.value)} required maxLength={80} />
        </label>
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => set_email(e.target.value)}
          required
          maxLength={254}
        />
      </label>
      {error && <div className="error">{error}</div>}
      <div>
        <button type="submit" disabled={busy}>
          {busy ? 'Creating...' : 'Create user'}
        </button>
      </div>
    </form>
  );
}

interface Tenant {
  tenant_id: string;
  name: string;
  name_normalized: string;
  status: string;
  phone?: string | null;
  business_timezone?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Common US IANA zones for the tenant timezone editor (server validates any IANA value). */
const TIMEZONE_OPTIONS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
];

interface User {
  user_id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  email: string;
  email_domain: string;
  role: string;
  status: string;
  verified_phones?: string[];
  apps: string[];
  created_at: string | null;
  updated_at: string | null;
}

function format_dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

export default function TenantDetail() {
  const { tenant_id = '' } = useParams<{ tenant_id: string }>();
  const qc = useQueryClient();

  const tenantQuery = useQuery({
    queryKey: ['tenant', tenant_id],
    queryFn: () => api.get<Tenant>(`/api/tenants/${tenant_id}`),
  });
  const usersQuery = useQuery({
    queryKey: ['tenant', tenant_id, 'users'],
    queryFn: () =>
      api.get<{ users: User[] }>(`/api/tenants/${tenant_id}/users`),
  });

  const [editing, set_editing] = useState(false);
  const [show_add_user, set_show_add_user] = useState(false);
  const [draft_name, set_draft_name] = useState('');
  const [save_busy, set_save_busy] = useState(false);
  const [save_error, set_save_error] = useState<string | null>(null);

  function start_edit() {
    set_draft_name(tenantQuery.data?.name ?? '');
    set_save_error(null);
    set_editing(true);
  }

  async function save_name(e: FormEvent) {
    e.preventDefault();
    set_save_busy(true);
    set_save_error(null);
    try {
      await api.patch(`/api/tenants/${tenant_id}`, { name: draft_name.trim() });
      qc.invalidateQueries({ queryKey: ['tenant', tenant_id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      set_editing(false);
    } catch (err) {
      set_save_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_save_busy(false);
    }
  }

  // Business timezone — receptionist confirmations (captomi-office) render in it; defaults to
  // Pacific on the office side when unset, so this is an override for non-Pacific tenants.
  const [editing_tz, set_editing_tz] = useState(false);
  const [draft_tz, set_draft_tz] = useState('America/Los_Angeles');
  const [tz_busy, set_tz_busy] = useState(false);
  const [tz_error, set_tz_error] = useState<string | null>(null);

  async function save_tz(e: FormEvent) {
    e.preventDefault();
    set_tz_busy(true);
    set_tz_error(null);
    try {
      await api.patch(`/api/tenants/${tenant_id}`, { business_timezone: draft_tz });
      qc.invalidateQueries({ queryKey: ['tenant', tenant_id] });
      set_editing_tz(false);
    } catch (err) {
      set_tz_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_tz_busy(false);
    }
  }

  return (
    <div className="container">
      <p style={{ marginTop: 0 }}>
        <Link to="/tenants" className="muted">
          ← Tenants
        </Link>
      </p>

      {tenantQuery.isLoading && <p>Loading tenant...</p>}
      {tenantQuery.error && (
        <p className="error">
          Failed to load tenant:{' '}
          {tenantQuery.error instanceof ApiError
            ? tenantQuery.error.reason
            : String(tenantQuery.error)}
        </p>
      )}
      {tenantQuery.data && (
        <>
          {editing ? (
            <form
              onSubmit={save_name}
              style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}
            >
              <input
                value={draft_name}
                onChange={(e) => set_draft_name(e.target.value)}
                required
                maxLength={120}
                autoFocus
                style={{ fontSize: 22, fontWeight: 600, padding: '6px 10px', flex: 1 }}
              />
              <button type="submit" disabled={save_busy}>
                {save_busy ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => set_editing(false)}
                disabled={save_busy}
              >
                Cancel
              </button>
            </form>
          ) : (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <h1 style={{ margin: 0 }}>{tenantQuery.data.name}</h1>
              <button
                className="secondary"
                onClick={start_edit}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                Edit name
              </button>
            </div>
          )}
          {save_error && (
            <div className="error" style={{ marginBottom: 16 }}>
              Failed to save: {save_error}
            </div>
          )}
          <div className="card" style={{ marginBottom: 24 }}>
            <KV>
              <KVRow
                label="Tenant ID"
                value={<code>{tenantQuery.data.tenant_id}</code>}
              />
              <KVRow
                label="Name normalized"
                value={<code>{tenantQuery.data.name_normalized}</code>}
              />
              <KVRow label="Status" value={tenantQuery.data.status} />
              <KVRow
                label="Office phone"
                value={
                  tenantQuery.data.phone ? (
                    <code>{tenantQuery.data.phone}</code>
                  ) : (
                    <span className="muted">— (not set)</span>
                  )
                }
              />
              <KVRow
                label="Business timezone"
                value={
                  editing_tz ? (
                    <form
                      onSubmit={save_tz}
                      style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                    >
                      <select value={draft_tz} onChange={(e) => set_draft_tz(e.target.value)}>
                        {TIMEZONE_OPTIONS.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz}
                          </option>
                        ))}
                      </select>
                      <button type="submit" disabled={tz_busy}>
                        {tz_busy ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => set_editing_tz(false)}
                        disabled={tz_busy}
                      >
                        Cancel
                      </button>
                      {tz_error && <span className="error">{tz_error}</span>}
                    </form>
                  ) : (
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {tenantQuery.data.business_timezone ? (
                        <code>{tenantQuery.data.business_timezone}</code>
                      ) : (
                        <span className="muted">— (defaults to America/Los_Angeles)</span>
                      )}
                      <button
                        className="secondary"
                        style={{ padding: '2px 8px', fontSize: 12 }}
                        onClick={() => {
                          set_draft_tz(
                            tenantQuery.data?.business_timezone ?? 'America/Los_Angeles',
                          );
                          set_tz_error(null);
                          set_editing_tz(true);
                        }}
                      >
                        Edit
                      </button>
                    </span>
                  )
                }
              />
              <KVRow
                label="Created"
                value={format_dt(tenantQuery.data.created_at)}
              />
              <KVRow
                label="Updated"
                value={format_dt(tenantQuery.data.updated_at)}
              />
            </KV>
          </div>
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
        <h2 style={{ margin: 0 }}>Users</h2>
        <button
          className="secondary"
          style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}
          onClick={() => set_show_add_user((v) => !v)}
        >
          {show_add_user ? 'Cancel' : 'Add user to this tenant'}
        </button>
      </div>
      {show_add_user && (
        <AddUserToTenantForm
          tenant_id={tenant_id}
          on_created={() => {
            qc.invalidateQueries({ queryKey: ['tenant', tenant_id, 'users'] });
            qc.invalidateQueries({ queryKey: ['tenants'] });
            set_show_add_user(false);
          }}
        />
      )}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 12 }}>
        {usersQuery.isLoading && <p style={{ padding: 24, margin: 0 }}>Loading users...</p>}
        {usersQuery.error && (
          <p className="error" style={{ margin: 24 }}>
            Failed to load users:{' '}
            {usersQuery.error instanceof ApiError
              ? usersQuery.error.reason
              : String(usersQuery.error)}
          </p>
        )}
        {usersQuery.data && (
          <DataTable
            rows={usersQuery.data.users}
            columns={[
              {
                header: 'Name',
                cell: (u) => (
                  <Link to={`/tenants/${tenant_id}/users/${u.user_id}`}>
                    <strong>
                      {u.first_name} {u.last_name}
                    </strong>
                  </Link>
                ),
              },
              {
                header: 'Apps',
                cell: (u) =>
                  u.apps.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {u.apps.map((a) => (
                        <code
                          key={a}
                          style={{
                            background: 'var(--green-50)',
                            color: 'var(--green-700)',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          {a}
                        </code>
                      ))}
                    </span>
                  ),
              },
              { header: 'Email', cell: (u) => u.email },
              {
                header: 'Verified phones',
                cell: (u) =>
                  (u.verified_phones?.length ?? 0) === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(u.verified_phones ?? []).map((p) => (
                        <code key={p} style={{ fontSize: 12 }}>
                          {p}
                        </code>
                      ))}
                    </span>
                  ),
              },
              { header: 'Role', cell: (u) => u.role },
              { header: 'Status', cell: (u) => u.status },
              {
                header: 'Created',
                cell: (u) =>
                  u.created_at
                    ? new Date(u.created_at).toLocaleDateString()
                    : '—',
              },
            ]}
            empty_message="No users in this tenant yet."
          />
        )}
      </div>

      <h2 style={{ marginTop: 32 }}>App data — captomi-office</h2>
      <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
        Schema-driven editor (tenant-scoped). Fields + edit policy come from
        <code> @captomi/captomi-office-schema</code>; read-only fields are system-managed.
      </p>
      <div className="card" style={{ marginBottom: 24 }}>
        <SchemaForm tenant_id={tenant_id} app_code="captomi_office" />
      </div>
      <TenantOfficeActions
        tenant_id={tenant_id}
        user_id={usersQuery.data?.users?.[0]?.user_id ?? null}
      />

      <h2 style={{ marginTop: 32 }}>Usage</h2>
      <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
        captomi-office minute + message usage for the current cycle.
      </p>
      <UsagePanel tenant_id={tenant_id} />

      <h2 style={{ marginTop: 32 }}>Bookings</h2>
      <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
        captomi-office bookings for this tenant.
      </p>
      <BookingsList pinned_tenant_id={tenant_id} />
    </div>
  );
}
